import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import Accordion from '@components/common/Accordion/Accordion';
import EmptyBlock from '@components/common/EmptyBlock/EmptyBlock';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import useTranslation from '@hooks/useTranslation';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import { equipmentsTypeNames } from '../../constants/equipmentsTypeNames';
import { useHardwareOverviewDetailsQuery } from '../../services/useHardwareOverviewDetailsQuery';
import {
  selectHODetailsOpenedKeys,
  selectHODetailsSearchValue,
  useHODetailsActions,
} from '../../store/useHODetailsStore';
import EquipmentCard from '../EquipmentCard/EquipmentCard';
import HardwareEquipmentsByLoc from '../HardwareEquipmentsByLoc/HardwareEquipmentsByLoc';
import HardwareOverviewDetailsSkeleton from './HardwareOverviewDetailsSkeleton';
import style from './style.module.scss';

const hasSplitKeys = {
  gateways: true,
  repeaters: true,
  nodes: true,
};

const HardwareOverviewDetailsBlock = () => {
  const { t } = useTranslation();

  const { setHODetailsOpenedKeys } = useHODetailsActions();

  const locationSelectValue = selectAppLocationSelectValue();
  const searchValue = selectHODetailsSearchValue();
  const openedKeys = selectHODetailsOpenedKeys();

  const [debouncedValue] = useDebouncedValue(searchValue, 500);

  const { data: hardwareDetailsData, isRefetching } = useHardwareOverviewDetailsQuery({
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    searchValue: debouncedValue,
  });

  if (!isRefetching && !hardwareDetailsData?.equipmentsTotal) {
    return <EmptyBlock text={t('Nothing was found.')} />;
  }

  return (
    <div className={style.blockWrapper}>
      {isRefetching ? (
        <HardwareOverviewDetailsSkeleton />
      ) : (
        hardwareDetailsData?.equipmentsTypeList.map((equipmentType) => (
          <div key={uuidv4()} className={style.equipmentTypeItem}>
            <Accordion
              title={t(equipmentsTypeNames[equipmentType as keyof typeof equipmentsTypeNames])}
              count={`${hardwareDetailsData.equipments[`${equipmentType}_ai_total`]}/${
                hardwareDetailsData.equipments[`${equipmentType}_total`]
              }`}
              isOpen={!!searchValue || openedKeys[equipmentType]}
              afterChangeShowContent={(value) => setHODetailsOpenedKeys(equipmentType, value)}
            >
              <>
                {hasSplitKeys[equipmentType as keyof typeof hasSplitKeys] && (
                  <div className={style.areaWrapper}>
                    {(
                      hardwareDetailsData.equipments[equipmentType as keyof typeof hasSplitKeys].other || []
                    ).map((equipment) => (
                      <EquipmentCard key={uuidv4()} equipmentInfo={equipment} keyData={equipmentType} />
                    ))}
                  </div>
                )}
                <HardwareEquipmentsByLoc
                  keyData={equipmentType}
                  equipmentsListByLoc={
                    hardwareDetailsData.equipments[equipmentType as keyof typeof hasSplitKeys].by_location ||
                    hardwareDetailsData.equipments[equipmentType as keyof typeof equipmentsTypeNames] ||
                    []
                  }
                />
              </>
            </Accordion>
          </div>
        ))
      )}
    </div>
  );
};

export default HardwareOverviewDetailsBlock;
